import { followUriBuilder } from 'pubky-app-specs';
import type {
  TEnsureModerationFollowParams,
  TUserApplicationFollowParams,
  TUserCountsOrFetchResult,
  TUserSocialGraphStatusResult,
} from '@/application/user/user.types';
import { USER_TAGS_PER_PAGE } from '@/config/tags';
import type { TReadProfileParams } from '@/controllers/profile/profile.types';
import type { TFetchUserDetailsParams, TFetchUserParams, TPubkyListParams } from '@/controllers/user/user.type';
import { ValidationErrorCode } from '@/libs/error/error.codes';
import { Err } from '@/libs/error/error.factories';
import { ErrorService } from '@/libs/error/error.types';
import { hasHttpStatus, isAppError, toAppError } from '@/libs/error/error.utils';
import { HttpMethod, HttpStatusCode } from '@/libs/http/http.types';
import { Logger } from '@/libs/logger/logger';
import type { Pubky } from '@/models/models.types';
import type { UserCountsModel } from '@/models/user/counts/userCounts';
import type { UserRelationshipsModelSchema } from '@/models/user/relationships/userRelationships.schema';
import { FollowNormalizer } from '@/pipes/follow/follow.normalizer';
import { HomeserverService } from '@/services/homeserver/homeserver';
import { LocalFollowService } from '@/services/local/follow/follow';
import { LocalProfileService } from '@/services/local/profile/profile';
import { LocalStreamUsersService } from '@/services/local/stream/users/users';
import { LocalTagCacheService } from '@/services/local/tag/tag-cache';
import { LocalUserTagService } from '@/services/local/tag/user/tag.user';
import { LocalUserService } from '@/services/local/user/user';
import type {
  NexusTag,
  NexusTaggers,
  NexusUserCounts,
  NexusUserDetails,
  NexusUserRelationship,
} from '@/services/nexus/nexus.types';
import { getNexusResponseStartedAt } from '@/services/nexus/nexus.utils';
import { NexusUserStreamService } from '@/services/nexus/stream/users/userStream';
import { NexusUserService } from '@/services/nexus/user/user';
import type { TUserTaggersParams } from '@/services/nexus/user/user.types';

export class UserApplication {
  /**
   * Full-user fetches in flight, keyed by user and viewer. Responsive profile surfaces
   * (desktop sidebar, mobile overview) stay mounted together, so several local-first hooks
   * can miss the cache for the same user at once; sharing one promise keeps the Nexus
   * request and the multi-table persist to a single run.
   */
  private static readonly inFlightFetches = new Map<
    string,
    { request: Promise<NexusUserDetails | null>; isCurrent?: () => boolean }
  >();

  /**
   * Get user details from local database
   * This is a read-only operation that queries the local cache
   */
  static async getDetails(param: TReadProfileParams): Promise<NexusUserDetails | null> {
    // Try to get from local database first
    // TODO: Throw an error and do not return null
    return await LocalUserService.readDetails(param);
  }

  /**
   * Bulk read multiple user details from local database.
   * Returns a Map for efficient lookup by user ID.
   */
  static async getManyDetails(param: TPubkyListParams): Promise<Map<Pubky, NexusUserDetails>> {
    return await LocalUserService.readBulkDetails(param);
  }

  /**
   * Retrieves user details from local database. If not found, fetches from Nexus API and persists to local database.
   * @param params - Parameters containing user ID
   * @returns Promise resolving to user details or null if not found
   */
  static async getOrFetchDetails({ userId, isCurrent }: TReadProfileParams & { isCurrent?: () => boolean }) {
    const userDetails = await LocalUserService.readDetails({ userId });
    if (userDetails) {
      return userDetails;
    }
    const nexusUserDetails = await NexusUserService.details({ user_id: userId });
    if (isCurrent && !isCurrent()) return null;
    await LocalProfileService.upsertDetails(nexusUserDetails);
    return await LocalUserService.readDetails({ userId });
  }

  /**
   * Fetches user details from Nexus and persists to local database (network-only, no local read).
   * Use instead of `getOrFetchDetails` when the caller already knows the user is not cached
   * (e.g. `useLocalFirstQuery` hook where `useLiveQuery` handles the local read).
   * @param params - Parameters containing user ID
   * @returns Promise resolving to user details or null if not found
   */
  static async fetchDetails({
    userId,
    isCurrent,
    ...options
  }: TFetchUserDetailsParams & { isCurrent?: () => boolean }) {
    const nexusUserDetails = await NexusUserService.details({ user_id: userId, ...options });
    if (isCurrent && !isCurrent()) return null;
    await LocalProfileService.upsertDetails(nexusUserDetails);
    return await LocalUserService.readDetails({ userId });
  }

  /**
   * Retrieves a full user entity (details, counts, relationships, tags, TTL, moderation) from local database.
   * If not found locally, fetches from Nexus batch endpoint and persists everything via stream persistence.
   *
   * Preferred over `getOrFetchDetails` / `getOrFetchCounts` when the caller needs the full entity cached.
   *
   * @param params - Parameters containing user ID and optional viewer ID (scopes the relationship row)
   * @returns Promise resolving to user details or null if not found
   */
  static async getOrFetch({
    userId,
    viewerId,
    isCurrent,
  }: TFetchUserParams & { isCurrent?: () => boolean }): Promise<NexusUserDetails | null> {
    // 1. Check local cache first
    const localDetails = await LocalUserService.readDetails({ userId });
    if (localDetails) {
      return localDetails;
    }

    // 2. Fetch full user from Nexus batch endpoint
    try {
      const fetchStartedAt = Date.now();
      const revisions = await LocalTagCacheService.captureRevisions('user', [userId]);
      const users = await NexusUserStreamService.fetchByIds({ user_ids: [userId], viewer_id: viewerId });

      if (isCurrent && !isCurrent()) return null;
      if (!users || users.length === 0) {
        Logger.warn('User not found on Nexus', { userId });
        return null;
      }

      // 3. Persist full user entity (details, counts, relationships, tags, TTL, moderation)
      await LocalStreamUsersService.persistUsers(users, { revisions, viewerId, isCurrent, fetchStartedAt });
    } catch (error) {
      Logger.warn('Failed to fetch user from Nexus', { userId, error });
      return null;
    }

    // 4. Return from local cache (now populated)
    return await LocalUserService.readDetails({ userId });
  }

  /**
   * Fetches a full user entity from Nexus batch endpoint and persists locally (network-only, no local read).
   * Use instead of `getOrFetch` when the caller already knows the user is not cached
   * (e.g. `useLocalFirstQuery` hook where `useLiveQuery` handles the local read).
   *
   * Concurrent calls for the same user and viewer share a single request and persist.
   *
   * @param params - Parameters containing user ID and optional viewer ID (scopes the relationship row)
   * @returns Promise resolving to user details or null if not found on Nexus
   */
  static async fetch({
    userId,
    viewerId,
    isCurrent,
  }: TFetchUserParams & { isCurrent?: () => boolean }): Promise<NexusUserDetails | null> {
    const key = `${userId}:${viewerId ?? ''}`;
    const inFlight = this.inFlightFetches.get(key);
    if (inFlight && (!inFlight.isCurrent || inFlight.isCurrent())) return await inFlight.request;

    const request = this.fetchAndPersist({ userId, viewerId, isCurrent }).finally(() => {
      if (this.inFlightFetches.get(key)?.request === request) this.inFlightFetches.delete(key);
    });
    this.inFlightFetches.set(key, { request, isCurrent });
    return await request;
  }

  private static async fetchAndPersist({
    userId,
    viewerId,
    isCurrent,
  }: TFetchUserParams & { isCurrent?: () => boolean }): Promise<NexusUserDetails | null> {
    try {
      const fetchStartedAt = Date.now();
      const revisions = await LocalTagCacheService.captureRevisions('user', [userId]);
      const users = await NexusUserStreamService.fetchByIds({ user_ids: [userId], viewer_id: viewerId });

      if (isCurrent && !isCurrent()) return null;
      if (!users || users.length === 0) {
        Logger.warn('User not found on Nexus', { userId });
        return null;
      }

      await LocalStreamUsersService.persistUsers(users, { revisions, viewerId, isCurrent, fetchStartedAt });
    } catch (error) {
      Logger.warn('Failed to fetch user from Nexus', { userId, error });
      return null;
    }

    return await LocalUserService.readDetails({ userId });
  }

  /**
   * Reads a user's social graph badge tier from local database.
   * Local-only read per ADR 0001 (get* methods don't call Nexus).
   * @param params - Parameters containing user ID
   * @returns `{ status }` once a full user view was cached, or null when the tier is still unknown
   */
  static async getSocialGraphStatus(params: TReadProfileParams): Promise<TUserSocialGraphStatusResult | null> {
    return await LocalUserService.readSocialGraphStatus(params);
  }

  /**
   * Retrieves user counts from local database.
   * Local-only read per ADR 0001 (get* methods don't call Nexus).
   * @param params - Parameters containing user ID
   * @returns Promise resolving to the cached row (includes `id`) or null if not found
   */
  static async getCounts({ userId }: TReadProfileParams): Promise<UserCountsModel | null> {
    return await LocalUserService.readCounts({ userId });
  }

  /**
   * Retrieves user counts from local database. If not found, fetches from Nexus API and persists to local database.
   * @param params - Parameters containing user ID
   * @returns Cached row (includes `id`) or Nexus payload or null if unavailable
   */
  static async getOrFetchCounts({
    userId,
    isCurrent,
  }: TReadProfileParams & { isCurrent?: () => boolean }): Promise<TUserCountsOrFetchResult | null> {
    const userCounts = await LocalUserService.readCounts({ userId });
    if (userCounts) {
      return userCounts;
    }

    try {
      const nexusUserCounts = await NexusUserService.counts({ user_id: userId });
      if (isCurrent && !isCurrent()) return null;
      await LocalProfileService.upsertCounts(userId, nexusUserCounts);
      return nexusUserCounts;
    } catch (error) {
      // Return null if user counts cannot be fetched (e.g., user not indexed yet)
      Logger.warn('Failed to fetch user counts from Nexus', { userId, error });
      return null;
    }
  }

  /**
   * Fetches user counts from Nexus and persists to local database (network-only, no local read).
   * Use instead of `getOrFetchCounts` when the caller already knows counts are not cached
   * (e.g. `useLocalFirstQuery` hook where `useLiveQuery` handles the local read).
   * @param params - Parameters containing user ID
   * @returns Promise resolving to user counts or null if fetch fails
   */
  static async fetchCounts({
    userId,
    isCurrent,
  }: TReadProfileParams & { isCurrent?: () => boolean }): Promise<NexusUserCounts | null> {
    try {
      const nexusUserCounts = await NexusUserService.counts({ user_id: userId });
      if (isCurrent && !isCurrent()) return null;
      await LocalProfileService.upsertCounts(userId, nexusUserCounts);
      return nexusUserCounts;
    } catch (error) {
      Logger.warn('Failed to fetch user counts from Nexus', { userId, error });
      return null;
    }
  }

  /**
   * Bulk read multiple user counts from local database.
   * Returns a Map for efficient lookup by user ID.
   */
  static async getManyCounts(param: TPubkyListParams): Promise<Map<Pubky, NexusUserCounts>> {
    return await LocalUserService.readBulkCounts(param);
  }

  /**
   * Get user relationships from local database
   * This is a read-only operation that queries the local cache
   */
  static async getRelationships(params: TReadProfileParams): Promise<NexusUserRelationship | null> {
    // TODO: Throw an error and do not return null
    return await LocalUserService.readRelationships(params);
  }

  /**
   * Bulk read multiple user relationships from local database.
   * Returns a Map for efficient lookup by user ID.
   * @param userIds - Array of user IDs to fetch relationships for
   * @returns Promise resolving to a Map of user ID to relationship data
   */
  static async getManyRelationships(param: TPubkyListParams): Promise<Map<Pubky, UserRelationshipsModelSchema>> {
    return await LocalUserService.readBulkRelationships(param);
  }

  /**
   * Follow syncs in flight, keyed by follower and followee. Overlapping commits for one pair
   * (the same user on two surfaces, or the sign-in moderation follow next to a manual one) run
   * in order, so an older failed sync compensates before a newer one writes; the `changed` gate
   * in {@link syncFollow} then keeps a queued no-op from undoing the commit that came before it.
   */
  private static readonly inFlightFollows = new Map<string, Promise<void>>();

  /**
   * Handles following or unfollowing a user.
   * Writes the relationship locally first and syncs it to the homeserver; a failed sync runs the
   * inverse local write so the live queries behind the Follow button, counts and friendship state
   * revert instead of diverging from the homeserver (#2065).
   * @param params - Parameters containing event type, URLs, JSON data, and user IDs
   */
  static async commitFollow(params: TUserApplicationFollowParams): Promise<void> {
    const key = `${params.follower}:${params.followee}`;
    const previous = this.inFlightFollows.get(key) ?? Promise.resolve();
    const run = previous.catch(() => undefined).then(() => this.syncFollow(params));
    this.inFlightFollows.set(key, run);
    try {
      await run;
    } finally {
      if (this.inFlightFollows.get(key) === run) this.inFlightFollows.delete(key);
    }
  }

  private static async syncFollow({
    eventType,
    followUrl,
    followJson,
    follower,
    followee,
    signal,
  }: TUserApplicationFollowParams): Promise<void> {
    if (signal?.aborted) return;

    // A write that found the relationship already in its target state has nothing to compensate:
    // rolling it back would undo a follow the homeserver already holds.
    let changed = false;
    if (eventType === HttpMethod.PUT) {
      changed = await LocalFollowService.create({ follower, followee });
    } else if (eventType === HttpMethod.DELETE) {
      changed = await LocalFollowService.delete({ follower, followee });
    }

    if (signal?.aborted) return;

    try {
      await HomeserverService.request({ method: eventType, url: followUrl, bodyJson: followJson });
    } catch (error) {
      // An already absent homeserver record agrees with the optimistic unfollow.
      if (eventType === HttpMethod.DELETE && hasHttpStatus(error, HttpStatusCode.NOT_FOUND)) return;
      // A torn-down session skips the compensation: its local rows no longer belong to this flow.
      if (changed && !signal?.aborted) {
        try {
          if (eventType === HttpMethod.PUT) {
            await LocalFollowService.delete({ follower, followee });
          } else if (eventType === HttpMethod.DELETE) {
            await LocalFollowService.create({ follower, followee });
          }
        } catch (rollbackError) {
          if (!isAppError(rollbackError))
            Logger.error('Failed to rollback local follow write', { eventType, follower, followee, rollbackError });
        }
      }
      throw toAppError(error, ErrorService.Homeserver, 'commitFollow');
    }
  }

  /**
   * Applies the moderation-bot default follow once per account and bot: skipped when settings already
   * record the configured bot, so a later manual unfollow is never undone. The canonical follow
   * resource makes retries idempotent.
   * @returns The processed bot Pubky when the caller should persist it, otherwise undefined
   */
  static async ensureModerationFollow({
    follower,
    moderationId,
    moderationBot,
    signal,
  }: TEnsureModerationFollowParams): Promise<Pubky | undefined> {
    if (!moderationId || follower === moderationId || signal?.aborted || moderationBot === moderationId) {
      return undefined;
    }

    const { meta, follow } = FollowNormalizer.to({ follower, followee: moderationId });
    const expectedFollowUrl = followUriBuilder(follower, moderationId);
    if (meta.url !== expectedFollowUrl) {
      throw Err.validation(ValidationErrorCode.INVALID_INPUT, 'Follow resource belongs to an unexpected account', {
        service: ErrorService.PubkyAppSpecs,
        operation: 'ensureModerationFollow',
        context: { follower, moderationId, followUrl: meta.url },
      });
    }

    const followExists = await HomeserverService.exists(meta.url);
    if (signal?.aborted) return undefined;

    if (!followExists) {
      await this.commitFollow({
        eventType: HttpMethod.PUT,
        followUrl: meta.url,
        followJson: follow.toJson(),
        follower,
        followee: moderationId,
        signal,
      });
    }

    if (signal?.aborted) return undefined;
    return moderationId;
  }

  /**
   * Retrieves taggers for a specific tag label on a user from the nexus service.
   * @param params - Parameters containing user ID, label, and pagination options
   * @returns Promise resolving to the users who tagged the user with the specified label
   */
  static async fetchTaggers(params: TUserTaggersParams): Promise<NexusTaggers> {
    return await NexusUserService.taggers(params);
  }

  /**
   * Bulk read multiple user tags with local-first strategy.
   * First reads from local cache, then fetches missing from Nexus API.
   * @param userIds - Array of user IDs to fetch tags for
   * @returns Promise resolving to a Map of user ID to tags array
   */
  static async getManyTagsOrFetch({
    userIds,
    viewerId,
    isCurrent,
  }: TPubkyListParams & { viewerId?: Pubky; isCurrent?: () => boolean }): Promise<Map<Pubky, NexusTag[]>> {
    if (userIds.length === 0) return new Map();

    // 1. Find users without cached tags
    const cacheMissUserIds = await LocalUserTagService.getNotPersistedUserTagsInCache(userIds);

    // 2. Fetch missing from API (parallel requests)
    if (cacheMissUserIds.length > 0) {
      await this.fetchMissingUserTagsFromNexus(cacheMissUserIds, viewerId, isCurrent);
    }

    // 3. Return all tags from cache (now populated with fetched data)
    return await LocalUserService.readBulkTags({ userIds });
  }

  /**
   * Fetch missing user tags from Nexus API and persist to cache.
   * The window is scoped to the viewer and persisted as theirs, so opening the
   * profile later reuses it instead of forcing a refresh for a viewer change.
   * @param cacheMissUserIds - Array of user IDs that need tags fetched
   */
  private static async fetchMissingUserTagsFromNexus(
    cacheMissUserIds: Pubky[],
    viewerId?: Pubky,
    isCurrent?: () => boolean,
  ) {
    if (cacheMissUserIds.length === 0) return;

    const fetchPromises = cacheMissUserIds.map(async (userId) => {
      try {
        const revisions = await LocalTagCacheService.captureRevisions('user', [userId]);
        const tags = await NexusUserService.tags({
          user_id: userId,
          viewer_id: viewerId,
          skip_tags: 0,
          limit_tags: USER_TAGS_PER_PAGE,
        });
        await LocalUserService.upsertTags(userId, tags, {
          revisions,
          isCurrent,
          validatedAt: getNexusResponseStartedAt(tags),
          viewerId,
        });
      } catch {
        // Silently fail for individual user - they'll just have no tags
      }
    });

    await Promise.all(fetchPromises);
  }
}
